import { useMemo } from 'react';
import { useGLTF } from '@react-three/drei';
import * as THREE from 'three';

export interface ShipProps {
  readonly modelPath: string;
  readonly position?: [number, number, number];
  readonly rotation?: [number, number, number];
  readonly targetSize?: number;
}

const DEFAULT_POSITION: [number, number, number] = [0, 0, 0];
const DEFAULT_ROTATION: [number, number, number] = [0, 0, 0];
const DEFAULT_TARGET_SIZE = 5;

export function normalizeScale(scene: THREE.Object3D, targetSize: number): number {
  const box = new THREE.Box3().setFromObject(scene);
  const size = new THREE.Vector3();
  box.getSize(size);
  const maxDimension = Math.max(size.x, size.y, size.z);
  if (maxDimension === 0) {
    return 1;
  }
  return targetSize / maxDimension;
}

function ShipModel({ modelPath, position, rotation, targetSize }: Required<ShipProps>) {
  const { scene } = useGLTF(modelPath);

  const clonedScene = useMemo(() => scene.clone(), [scene]);
  const scale = useMemo(() => normalizeScale(clonedScene, targetSize), [clonedScene, targetSize]);

  return (
    <group position={position} rotation={rotation}>
      {/* GLB nose is at +Z; rotate 180° to match -Z nose convention */}
      <group rotation={[0, Math.PI, 0]}>
        <primitive object={clonedScene} scale={[scale, scale, scale]} />
      </group>
    </group>
  );
}

export function Ship({
  modelPath,
  position = DEFAULT_POSITION,
  rotation = DEFAULT_ROTATION,
  targetSize = DEFAULT_TARGET_SIZE,
}: ShipProps) {
  return (
    <ShipModel
      modelPath={modelPath}
      position={position}
      rotation={rotation}
      targetSize={targetSize}
    />
  );
}

function PlaceholderShip({
  position = DEFAULT_POSITION,
  rotation = DEFAULT_ROTATION,
  targetSize = DEFAULT_TARGET_SIZE,
}: Omit<ShipProps, 'modelPath'>) {
  const halfSize = targetSize / 2;

  return (
    <group position={position} rotation={rotation}>
      <mesh>
        <boxGeometry args={[halfSize, halfSize * 0.3, halfSize * 1.5]} />
        <meshStandardMaterial
          color="#00ff88"
          wireframe={true}
          transparent={true}
          opacity={0.7}
        />
      </mesh>
      <mesh position={[0, 0, -halfSize * 0.9]}>
        <coneGeometry args={[halfSize * 0.3, halfSize * 0.6, 4]} />
        <meshStandardMaterial
          color="#00ff88"
          wireframe={true}
          transparent={true}
          opacity={0.7}
        />
      </mesh>
    </group>
  );
}

export { PlaceholderShip };
